import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

interface MenuItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

interface Props {
  items: MenuItem[];
}

const POPUP_WIDTH = 180;
const VERTICAL_GAP = 6;

/**
 * Three-dots overflow menu — used on dashboard rows for per-script actions
 * (Duplicate, Delete, etc).
 *
 * The popup is rendered via a portal to document.body with position: fixed,
 * so it escapes any stacking contexts created by sibling rows. Earlier
 * implementations rendered it inside the row's <li>, where the next row
 * would paint over the popup despite z-30.
 */
export default function RowMenu({ items }: Props) {
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  function positionPopup() {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPopupStyle({
      position: 'fixed',
      top: rect.bottom + VERTICAL_GAP,
      right: window.innerWidth - rect.right,
      width: POPUP_WIDTH,
      zIndex: 60,
    });
  }

  function toggle(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!open) positionPopup();
    setOpen((v) => !v);
  }

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        popupRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onScroll() {
      // Reposition on scroll so the popup tracks the trigger.
      positionPopup();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="More actions"
        title="More actions"
        onClick={toggle}
        className="
          p-2 rounded-sm text-faint
          opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100
          hover:text-fg hover:bg-elevated
          transition-all duration-200
        "
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="5" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="19" cy="12" r="1.6" />
        </svg>
      </button>

      {open &&
        popupStyle &&
        createPortal(
          <div
            ref={popupRef}
            style={popupStyle}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="
              py-1
              bg-card border border-line rounded-sm
              shadow-flare-lg/30
              animate-rise-subtle
            "
          >
            {items.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(false);
                  item.onClick();
                }}
                className={`
                  w-full text-left px-3 py-2 transition-colors duration-150
                  font-medium text-[13px]
                  hover:bg-elevated
                  ${item.destructive ? 'text-accent' : 'text-fg'}
                `}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
